import type { FeatureFlagKey } from '@discover/domain'
import { useQuery } from '@tanstack/react-query'
import type { TabItem } from '@viapeople/components'
import { AddIcon, Button, Dialog, InfoIcon, Modal, ModuleHeader, TabBar, Text } from '@viapeople/components'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { type Column, GenericTable } from '../../components/GenericTable'
import { useToast } from '../../utils/ToastContext'
import { trpc, trpcClient } from '../../utils/trpc'
import { EnabledOrganizationsCountCell } from './components/EnabledOrganizationsCountCell'
import { FeatureFlagForm } from './components/FeatureFlagForm'
import { FeatureFlagHistoryTab } from './components/FeatureFlagHistoryTab'
import { FeatureFlagOrganizationsTab } from './components/FeatureFlagOrganizationsTab'
import { type SearchParams, useFeatureFlagsManagementStore } from './store'
import type { FeatureFlag, FeatureFlagFormInput } from './types'

// Tooltip component for feature flag descriptions
const DescriptionTooltip = ({ description }: { description: string }) => {
	const [showTooltip, setShowTooltip] = useState(false)

	return (
		<div
			role='tooltip'
			className='relative inline-block'
			onMouseEnter={() => setShowTooltip(true)}
			onMouseLeave={() => setShowTooltip(false)}>
			<InfoIcon size={16} className='text-neutral-400 hover:text-neutral-600 cursor-help' />
			{showTooltip && (
				<div className='absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-[9999] w-64 p-2 bg-neutral-800 text-white text-xs rounded shadow-lg whitespace-normal'>
					{description}
				</div>
			)}
		</div>
	)
}

let mounted = false
export const FeatureFlagsPage = () => {
	const toast = useToast()
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
	const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null)
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
	const [flagToDelete, setFlagToDelete] = useState<FeatureFlag | null>(null)
	const [activeTab, setActiveTab] = useState('attributes')
	const [mainPageTab, setMainPageTab] = useState<'flags' | 'history'>('flags')
	const [enabledOrganizationsCount, setEnabledOrganizationsCount] = useState(0)
	const [organizationChanges, setOrganizationChanges] = useState<Record<string, boolean>>({})
	const [isCreating, setIsCreating] = useState(false)
	const [isUpdating, setIsUpdating] = useState(false)
	const [isDeleting, setIsDeleting] = useState(false)
	const [isBulkToggling, setIsBulkToggling] = useState(false)

	const fetchFlags = useFeatureFlagsManagementStore((s) => s.fetchFlags)
	const flags = useFeatureFlagsManagementStore((s) => s.flags)
	const initialize = useFeatureFlagsManagementStore((s) => s.initialize)
	const setSearchParams = useFeatureFlagsManagementStore((s) => s.setSearchParams)
	const availableEntries = useFeatureFlagsManagementStore((s) => s.availableEntries)
	const isLoadingAvailableEntries = useFeatureFlagsManagementStore((s) => s.isLoadingAvailableEntries)
	const availableEntriesError = useFeatureFlagsManagementStore((s) => s.availableEntriesError)
	const fetchAvailableEntries = useFeatureFlagsManagementStore((s) => s.fetchAvailableEntries)

	useEffect(() => {
		if (!mounted) {
			mounted = true
			initialize()
			// Pre-fetch dropdown so the Create button can be disabled accurately
			// before the modal is opened, and so the modal opens without a flash.
			fetchAvailableEntries()
		}
	}, [initialize, fetchAvailableEntries])

	// Refresh the dropdown when the create modal re-opens to stay in sync with
	// any rows created/deleted in the same session.
	useEffect(() => {
		if (isCreateModalOpen && !editingFlag) {
			fetchAvailableEntries()
		}
	}, [isCreateModalOpen, editingFlag, fetchAvailableEntries])

	useEffect(() => {
		if (availableEntriesError) {
			toast.showToast(availableEntriesError, 'error')
		}
	}, [availableEntriesError, toast])

	const createFeatureFlag = useCallback(
		async (data: FeatureFlagFormInput) => {
			setIsCreating(true)
			try {
				if (data.key === '') throw new Error('Feature flag key is required')
				const { key, ...rest } = data
				const result = await trpcClient.admin.featureFlagsManagementPage.create.mutate({ ...rest, key })
				toast.showToast('Feature flag created successfully')
				await fetchFlags()
				await fetchAvailableEntries()
				return result
			} catch (error) {
				toast.showToast(error instanceof Error ? error.message : 'Failed to create feature flag', 'error')
				throw error
			} finally {
				setIsCreating(false)
			}
		},
		[fetchFlags, fetchAvailableEntries, toast],
	)

	const updateFeatureFlag = useCallback(
		async (id: string, data: FeatureFlagFormInput) => {
			setIsUpdating(true)
			try {
				await trpcClient.admin.featureFlagsManagementPage.update.mutate({ id, ...data })
				toast.showToast('Feature flag updated successfully')
				await fetchFlags()
			} catch (error) {
				toast.showToast(error instanceof Error ? error.message : 'Failed to update feature flag', 'error')
				throw error
			} finally {
				setIsUpdating(false)
			}
		},
		[fetchFlags, toast],
	)

	const deleteFeatureFlag = useCallback(
		async (id: string) => {
			setIsDeleting(true)
			try {
				await trpcClient.admin.featureFlagsManagementPage.delete.mutate({ id })
				toast.showToast('Feature flag deleted successfully')
				await fetchFlags()
				await fetchAvailableEntries()
			} catch (error) {
				toast.showToast(error instanceof Error ? error.message : 'Failed to delete feature flag', 'error')
				throw error
			} finally {
				setIsDeleting(false)
			}
		},
		[fetchFlags, fetchAvailableEntries, toast],
	)

	const bulkToggle = useCallback(
		async (input: { flagId: string; organizationIds: string[]; enabled: boolean }) => {
			setIsBulkToggling(true)
			try {
				await trpcClient.admin.organizationFeatureFlagsTab.bulkToggle.mutate(input)
				toast.showToast('Feature flag organizations updated successfully')
				await fetchFlags()
			} catch (error) {
				toast.showToast(error instanceof Error ? error.message : 'Failed to update feature flag organizations', 'error')
				throw error
			} finally {
				setIsBulkToggling(false)
			}
		},
		[fetchFlags, toast],
	)

	const columns: Column<FeatureFlag>[] = [
		{
			field: 'key',
			label: 'Key',
			colSpan: 2,
			render: (flag: FeatureFlag) => (
				<Text variant='xsmall' weight='semibold' className='group-hover:text-primary-600'>
					{flag.key}
				</Text>
			),
		},
		{
			field: 'name',
			label: 'Name',
			colSpan: 2,
			render: (flag: FeatureFlag) => (
				<div className='flex items-center gap-2'>
					<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
						{flag.name}
					</Text>
					{flag.description && <DescriptionTooltip description={flag.description} />}
				</div>
			),
		},
		{
			field: 'id',
			label: 'Enabled Organizations',
			colSpan: 1,
			sortable: false,
			render: (flag: FeatureFlag) => <EnabledOrganizationsCountCell flagId={flag.id} />,
		},
	]

	const handleCreateFeatureFlag = async (data: FeatureFlagFormInput) => {
		const newFlag = await createFeatureFlag(data)
		setEditingFlag(newFlag)
		setActiveTab('organizations')
	}

	const handleUpdateFeatureFlag = async (data: FeatureFlagFormInput) => {
		if (!editingFlag?.id) return
		await updateFeatureFlag(editingFlag.id, data)
		handleCloseModal()
	}

	const handleDeleteFeatureFlag = async () => {
		if (flagToDelete) {
			await deleteFeatureFlag(flagToDelete.id)
			setIsDeleteModalOpen(false)
			setFlagToDelete(null)
			setIsCreateModalOpen(false)
			setEditingFlag(null)
		}
	}

	const handleRowClick = (flag: FeatureFlag) => {
		setEditingFlag(flag)
		setActiveTab('attributes')
		setIsCreateModalOpen(true)
	}

	const handleCloseModal = () => {
		setIsCreateModalOpen(false)
		setEditingFlag(null)
		setActiveTab('attributes')
		setEnabledOrganizationsCount(0)
		setOrganizationChanges({})
	}

	const handleSaveOrganizations = async () => {
		if (!editingFlag?.id) {
			handleCloseModal()
			return
		}

		// If no changes, just close
		if (Object.keys(organizationChanges).length === 0) {
			handleCloseModal()
			return
		}

		try {
			// Get current organizations to compare
			const currentOrgs = await trpcClient.admin.featureFlagsManagementPage.getOrganizations.query({
				flagId: editingFlag.id,
			})

			if (!currentOrgs) {
				handleCloseModal()
				return
			}

			// Determine which organizations need to be updated
			const enabledOrgs: string[] = []
			const disabledOrgs: string[] = []

			Object.entries(organizationChanges).forEach(([orgId, enabled]) => {
				const currentOrg = currentOrgs.find((o: { id: string }) => o.id === orgId)
				const currentState = currentOrg?.enabled ?? false

				// Only update if state changed
				if (currentState !== enabled) {
					if (enabled) {
						enabledOrgs.push(orgId)
					} else {
						disabledOrgs.push(orgId)
					}
				}
			})

			// Bulk update enabled organizations first
			if (enabledOrgs.length > 0) {
				await bulkToggle({
					flagId: editingFlag.id,
					organizationIds: enabledOrgs,
					enabled: true,
				})
			}

			// Bulk update disabled organizations (only if enabled update succeeded or wasn't needed)
			if (disabledOrgs.length > 0) {
				await bulkToggle({
					flagId: editingFlag.id,
					organizationIds: disabledOrgs,
					enabled: false,
				})
			}

			handleCloseModal()
		} catch (error) {
			console.error('Error saving organizations:', error)
		}
	}

	// Get enabled organizations count for badge - query immediately when editing
	const { data: organizationsData } = useQuery({
		...trpc.admin.featureFlagsManagementPage.getOrganizations.queryOptions({
			flagId: editingFlag?.id || '',
		}),
		enabled: !!editingFlag?.id,
	})

	// Initialize badge count from server data when modal opens
	useEffect(() => {
		if (organizationsData && editingFlag) {
			const count = organizationsData.filter((org: { enabled: boolean }) => org.enabled).length
			setEnabledOrganizationsCount(count)
		} else if (!editingFlag) {
			setEnabledOrganizationsCount(0)
		}
	}, [organizationsData, editingFlag])

	const tabs: TabItem[] = [
		{ id: 'attributes', label: 'FF Attributes' },
		{
			id: 'organizations',
			label: 'Organizations',
			badge: enabledOrganizationsCount > 0 ? enabledOrganizationsCount : undefined,
		},
	]

	// Fetch data for history filters (must be before early return)
	const { data: allFeatureFlags = [] } = useQuery({
		...trpc.admin.featureFlagsHistoryPage.getAllForHistory.queryOptions(),
		enabled: mainPageTab === 'history',
	})

	// Extract unique organizations and users from history (we'll fetch this from history endpoint)
	// For now, we'll pass empty arrays and let the history component handle it
	const { data: historyData } = useQuery({
		...trpc.admin.featureFlagsHistoryPage.getHistory.queryOptions({
			page: 1,
			perPage: 1000, // Get all for extracting unique values
			sortBy: 'performedAt',
			order: 'desc',
		}),
		enabled: mainPageTab === 'history',
	})

	// Extract unique organizations and users from history
	const uniqueOrganizations = useMemo(() => {
		if (!historyData?.data) return []
		const orgMap = new Map<string, { id: string; name: string }>()
		historyData.data.forEach((entry) => {
			if (entry.organizationId && entry.organizationName && !orgMap.has(entry.organizationId)) {
				orgMap.set(entry.organizationId, {
					id: entry.organizationId,
					name: entry.organizationName,
				})
			}
		})
		return Array.from(orgMap.values()).sort((a, b) => a.name.localeCompare(b.name))
	}, [historyData])

	const uniqueUsers = useMemo(() => {
		if (!historyData?.data) return []
		const userMap = new Map<string, { id: string; email: string; firstName: string; lastName: string }>()
		historyData.data.forEach((entry) => {
			if (entry.performedBy && entry.performedByEmail && !userMap.has(entry.performedBy)) {
				userMap.set(entry.performedBy, {
					id: entry.performedBy,
					email: entry.performedByEmail,
					firstName: entry.performedByFirstName || '',
					lastName: entry.performedByLastName || '',
				})
			}
		})
		return Array.from(userMap.values()).sort((a, b) =>
			`${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`),
		)
	}, [historyData])

	const mainTabs: TabItem[] = [
		{ id: 'flags', label: 'Feature Flags' },
		{ id: 'history', label: 'History' },
	]

	return (
		<main className='w-full p-6 flex flex-col gap-4'>
			<ModuleHeader
				breadcrumb={[{ label: 'Feature Flags' }]}
				title='Feature Flags'
				buttons={
					mainPageTab === 'flags'
						? [
								{
									label: 'Create Feature Flag',
									icon: AddIcon,
									onClick: () => {
										setEditingFlag(null)
										setIsCreateModalOpen(true)
									},
									variant: 'primary',
									// Disabled when the registry is fully consumed so CSMs can't open
									// a modal that has no actionable keys to pick.
									disabled: !isLoadingAvailableEntries && availableEntries.length === 0,
								},
							]
						: []
				}
			/>
			<TabBar tabs={mainTabs} activeTab={mainPageTab} onChange={(tab) => setMainPageTab(tab as 'flags' | 'history')} />

			{mainPageTab === 'flags' && (
				<GenericTable<FeatureFlag, SearchParams>
					columns={columns}
					data={flags}
					idField='id'
					onRowClick={handleRowClick}
					onSearchChange={setSearchParams}
					showSearch
					showPagination
					emptyState={{
						icon: AddIcon,
						title: 'No feature flags created yet',
						description: 'Get started by creating a new feature flag',
						actionLabel: 'Create Feature Flag',
						onAction: () => {
							setEditingFlag(null)
							setIsCreateModalOpen(true)
						},
					}}
				/>
			)}

			{mainPageTab === 'history' && (
				<FeatureFlagHistoryTab featureFlags={allFeatureFlags} organizations={uniqueOrganizations} users={uniqueUsers} />
			)}

			{(isCreateModalOpen || editingFlag) && (
				<Modal
					isOpen
					onClose={handleCloseModal}
					title={editingFlag ? 'Edit Feature Flag' : 'Create Feature Flag'}
					buttons={[]}
					bodyClassName='flex flex-col max-h-[calc(100vh-200px)] overflow-hidden'>
					<div className='flex flex-col h-full min-h-0'>
						<div className='mb-4 flex-shrink-0'>
							<TabBar tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
						</div>

						<div className='flex-1 min-h-0 overflow-y-auto'>
							{activeTab === 'attributes' && (
								<FeatureFlagForm
									initialValues={{
										// editingFlag.key is typed as plain string from the DB row; the key field
										// is read-only in edit mode so any historical value is acceptable here.
										key: (editingFlag?.key ?? '') as FeatureFlagKey | '',
										name: editingFlag?.name ?? '',
										description: editingFlag?.description ?? '',
									}}
									onSubmit={editingFlag ? handleUpdateFeatureFlag : handleCreateFeatureFlag}
									isSubmitting={isCreating || isUpdating}
									isEditMode={!!editingFlag}
									availableEntries={availableEntries}
									isLoadingAvailableEntries={isLoadingAvailableEntries}
								/>
							)}

							{activeTab === 'organizations' &&
								(editingFlag ? (
									<FeatureFlagOrganizationsTab
										flagId={editingFlag.id}
										onEnabledCountChange={setEnabledOrganizationsCount}
										onOrganizationsChange={setOrganizationChanges}
									/>
								) : (
									<div className='p-4 text-center'>
										<Text variant='small' className='text-neutral-500'>
											Please create the feature flag first, then you can manage organizations.
										</Text>
									</div>
								))}
						</div>

						<div className='h-px bg-neutral-100 w-full mt-6 flex-shrink-0' />
						<div className='flex justify-between items-center pt-6 flex-shrink-0'>
							<div>
								{editingFlag && (
									<Button
										variant='destructive'
										onClick={() => {
											setFlagToDelete(editingFlag)
											setIsDeleteModalOpen(true)
										}}>
										Delete
									</Button>
								)}
							</div>
							<div className='flex gap-3'>
								<Button variant='secondary' onClick={handleCloseModal}>
									Cancel
								</Button>
								<Button
									variant='primary'
									isLoading={isCreating || isUpdating || isBulkToggling || isDeleting}
									onClick={async () => {
										if (activeTab === 'attributes') {
											const form = document.getElementById(
												editingFlag ? 'editFeatureFlagForm' : 'addFeatureFlagForm',
											) as HTMLFormElement
											form?.dispatchEvent(new Event('submit', { bubbles: true }))
										} else if (activeTab === 'organizations' && editingFlag) {
											// Save organization changes
											await handleSaveOrganizations()
										} else {
											// For new flags on organizations tab, just close
											handleCloseModal()
										}
									}}>
									{editingFlag ? 'Save changes' : 'Create'}
								</Button>
							</div>
						</div>
					</div>
				</Modal>
			)}

			<Dialog
				isOpen={isDeleteModalOpen}
				onClose={() => {
					setIsDeleteModalOpen(false)
					setFlagToDelete(null)
				}}
				onConfirm={handleDeleteFeatureFlag}
				title='Delete Feature Flag'
				message={`Are you sure you want to delete "${flagToDelete?.name}" (${flagToDelete?.key})? This will also remove all organization-specific overrides. This action cannot be reversed.`}
				type='delete'
				confirmLabel='Delete'
			/>
		</main>
	)
}
